import { useState, useEffect } from 'react';
import { PageElementConfig, getPageElementConfigs } from '../services/pageElements';

export const usePageElementConfig = (pageType: string, elementName: string, defaultVariant: string = 'unified') => {
  const [variant, setVariant] = useState<string>(defaultVariant);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadConfig = async () => {
      try {
        const configs = await getPageElementConfigs(pageType);
        if (active) {
          const config = configs.find(c => c.element_name === elementName);
          if (config) {
            setVariant(config.variant);
          }
        }
      } catch (error) {
        console.error(`Failed to load config for ${elementName}:`, error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      active = false;
    };
  }, [pageType, elementName, defaultVariant]);

  return { variant, loading };
};

export const useAllPageElementConfigs = (pageType: string) => {
  const [configs, setConfigs] = useState<Record<string, string>>({});
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [styles, setStyles] = useState<Record<string, Record<string, any>>>({});
  const [elementOrder, setElementOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    let active = true;

    // Helper function to apply DOM order
    const applyDOMOrder = (order: string[]) => {
      // Check if MyUIbrix viewport wrapper is active
      const viewportWrapper = document.querySelector('.myuibrix-viewport-wrapper');
      const container = viewportWrapper || document.querySelector('.container');
      if (!container) return;
      
      const sections = Array.from(container.querySelectorAll('[data-element]')) as HTMLElement[];
      const elementMap = new Map<string, HTMLElement>();
      sections.forEach(section => {
        const elementName = section.getAttribute('data-element');
        if (elementName) {
          elementMap.set(elementName, section);
        }
      });
      
      order.forEach((elementName) => {
        const element = elementMap.get(elementName);
        if (element && element.parentElement === container) {
          container.appendChild(element);
        }
      });
    };

    // Helper: inject style properties for each element into a single <style> tag
    // This ensures style applying/previewing works even if components do not spread getStyles()
    // Uses ultra-high specificity to override any component CSS
    const updateInjectedStyleProps = (stylesMap: Record<string, Record<string, any>>) => {
      try {
        const styleId = 'myuibrix-style-props';
        let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = styleId;
          // Insert at end of head for maximum specificity
          document.head.appendChild(styleEl);
        }

        const cssBlocks: string[] = [];
        const addDecl = (decls: string[], prop: string, val: any, unit?: 'px' | '') => {
          if (val === undefined || val === null || val === '') return;
          const needsPx = unit === 'px';
          const v = typeof val === 'number' && needsPx ? `${val}px` : `${val}`;
          decls.push(`${prop}: ${v} !important;`);
        };
        const toKebab = (key: string) => key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

        Object.entries(stylesMap || {}).forEach(([name, st]) => {
          if (!st || typeof st !== 'object') return;
          const decls: string[] = [];
          // Track known keys we handle explicitly so we can inject the rest generically
          const handled = new Set<string>();

          addDecl(decls, 'font-family', st.fontFamily);
          handled.add('fontFamily');
          addDecl(decls, 'font-size', st.fontSize, 'px');
          handled.add('fontSize');
          addDecl(decls, 'font-weight', st.fontWeight);
          handled.add('fontWeight');
          addDecl(decls, 'line-height', st.lineHeight);
          handled.add('lineHeight');
          addDecl(decls, 'letter-spacing', st.letterSpacing, 'px');
          handled.add('letterSpacing');
          addDecl(decls, 'text-transform', st.textTransform);
          handled.add('textTransform');
          addDecl(decls, 'color', st.color);
          handled.add('color');
          addDecl(decls, 'background-color', st.backgroundColor);
          handled.add('backgroundColor');
          addDecl(decls, 'padding-top', st.paddingTop, 'px');
          handled.add('paddingTop');
          addDecl(decls, 'padding-right', st.paddingRight, 'px');
          handled.add('paddingRight');
          addDecl(decls, 'padding-bottom', st.paddingBottom, 'px');
          handled.add('paddingBottom');
          addDecl(decls, 'padding-left', st.paddingLeft, 'px');
          handled.add('paddingLeft');
          addDecl(decls, 'margin-top', st.marginTop, 'px');
          handled.add('marginTop');
          addDecl(decls, 'margin-right', st.marginRight, 'px');
          handled.add('marginRight');
          addDecl(decls, 'margin-bottom', st.marginBottom, 'px');
          handled.add('marginBottom');
          addDecl(decls, 'margin-left', st.marginLeft, 'px');
          handled.add('marginLeft');
          // width/height may be numbers (px) or strings (%, auto, etc.)
          if (st.width !== undefined) addDecl(decls, 'width', st.width, typeof st.width === 'number' ? 'px' : '');
          handled.add('width');
          if (st.height !== undefined) addDecl(decls, 'height', st.height, typeof st.height === 'number' ? 'px' : '');
          handled.add('height');
          addDecl(decls, 'display', st.display);
          handled.add('display');
          addDecl(decls, 'grid-template-columns', st.gridTemplateColumns);
          handled.add('gridTemplateColumns');
          addDecl(decls, 'grid-template-rows', st.gridTemplateRows);
          handled.add('gridTemplateRows');
          addDecl(decls, 'grid-auto-flow', st.gridAutoFlow);
          handled.add('gridAutoFlow');
          addDecl(decls, 'grid-column-gap', st.gridColumnGap, 'px');
          handled.add('gridColumnGap');
          addDecl(decls, 'grid-row-gap', st.gridRowGap, 'px');
          handled.add('gridRowGap');
          addDecl(decls, 'align-items', st.alignItems);
          handled.add('alignItems');
          addDecl(decls, 'justify-items', st.justifyItems);
          handled.add('justifyItems');
          // Additional commonly used properties
          addDecl(decls, 'gap', st.gap, 'px');
          handled.add('gap');
          // Borders
          addDecl(decls, 'border', st.border);
          handled.add('border');
          if (st.borderTop !== undefined) addDecl(decls, 'border-top', st.borderTop);
          handled.add('borderTop');
          if (st.borderRight !== undefined) addDecl(decls, 'border-right', st.borderRight);
          handled.add('borderRight');
          if (st.borderBottom !== undefined) addDecl(decls, 'border-bottom', st.borderBottom);
          handled.add('borderBottom');
          if (st.borderLeft !== undefined) addDecl(decls, 'border-left', st.borderLeft);
          handled.add('borderLeft');
          if (st.borderRadius !== undefined) addDecl(decls, 'border-radius', st.borderRadius, typeof st.borderRadius === 'number' ? 'px' : '');
          handled.add('borderRadius');
          if (st.borderTopLeftRadius !== undefined) addDecl(decls, 'border-top-left-radius', st.borderTopLeftRadius, typeof st.borderTopLeftRadius === 'number' ? 'px' : '');
          handled.add('borderTopLeftRadius');
          if (st.borderTopRightRadius !== undefined) addDecl(decls, 'border-top-right-radius', st.borderTopRightRadius, typeof st.borderTopRightRadius === 'number' ? 'px' : '');
          handled.add('borderTopRightRadius');
          if (st.borderBottomLeftRadius !== undefined) addDecl(decls, 'border-bottom-left-radius', st.borderBottomLeftRadius, typeof st.borderBottomLeftRadius === 'number' ? 'px' : '');
          handled.add('borderBottomLeftRadius');
          if (st.borderBottomRightRadius !== undefined) addDecl(decls, 'border-bottom-right-radius', st.borderBottomRightRadius, typeof st.borderBottomRightRadius === 'number' ? 'px' : '');
          handled.add('borderBottomRightRadius');
          // Effects
          addDecl(decls, 'box-shadow', st.boxShadow);
          handled.add('boxShadow');
          addDecl(decls, 'opacity', st.opacity);
          handled.add('opacity');
          // Positioning
          addDecl(decls, 'position', st.position);
          handled.add('position');
          if (st.top !== undefined) addDecl(decls, 'top', st.top, typeof st.top === 'number' ? 'px' : '');
          handled.add('top');
          if (st.right !== undefined) addDecl(decls, 'right', st.right, typeof st.right === 'number' ? 'px' : '');
          handled.add('right');
          if (st.bottom !== undefined) addDecl(decls, 'bottom', st.bottom, typeof st.bottom === 'number' ? 'px' : '');
          handled.add('bottom');
          if (st.left !== undefined) addDecl(decls, 'left', st.left, typeof st.left === 'number' ? 'px' : '');
          handled.add('left');
          addDecl(decls, 'z-index', st.zIndex);
          handled.add('zIndex');
          // Overflow
          addDecl(decls, 'overflow', st.overflow);
          handled.add('overflow');
          addDecl(decls, 'overflow-x', st.overflowX);
          handled.add('overflowX');
          addDecl(decls, 'overflow-y', st.overflowY);
          handled.add('overflowY');
          // Alignment (flex/grid)
          addDecl(decls, 'justify-content', st.justifyContent);
          handled.add('justifyContent');
          addDecl(decls, 'align-content', st.alignContent);
          handled.add('alignContent');
          addDecl(decls, 'justify-self', st.justifySelf);
          handled.add('justifySelf');
          addDecl(decls, 'align-self', st.alignSelf);
          handled.add('alignSelf');
          addDecl(decls, 'place-items', st.placeItems);
          handled.add('placeItems');
          addDecl(decls, 'place-content', st.placeContent);
          handled.add('placeContent');
          addDecl(decls, 'place-self', st.placeSelf);
          handled.add('placeSelf');
          // Sizing constraints
          if (st.maxWidth !== undefined) addDecl(decls, 'max-width', st.maxWidth, typeof st.maxWidth === 'number' ? 'px' : '');
          handled.add('maxWidth');
          if (st.minWidth !== undefined) addDecl(decls, 'min-width', st.minWidth, typeof st.minWidth === 'number' ? 'px' : '');
          handled.add('minWidth');
          if (st.maxHeight !== undefined) addDecl(decls, 'max-height', st.maxHeight, typeof st.maxHeight === 'number' ? 'px' : '');
          handled.add('maxHeight');
          if (st.minHeight !== undefined) addDecl(decls, 'min-height', st.minHeight, typeof st.minHeight === 'number' ? 'px' : '');
          handled.add('minHeight');
          // Text alignment
          addDecl(decls, 'text-align', st.textAlign);
          handled.add('textAlign');

          // Generic fallback: inject any additional properties provided in styles
          Object.keys(st).forEach((k) => {
            if (k === 'customCSS') return;
            if (!handled.has(k)) {
              const val = (st as any)[k];
              if (val !== undefined && val !== null && val !== '') {
                addDecl(decls, toKebab(k), val);
              }
            }
          });

          if (decls.length > 0) {
            // Ultra-high specificity selector: body > .container [data-element="name"]
            // This overrides most component CSS without needing to modify every component
            cssBlocks.push(`
body [data-element="${name}"],
.container [data-element="${name}"],
[data-element="${name}"].chakra-box,
[data-element="${name}"].section,
[data-element="${name}"] {
  ${decls.join('\n  ')}
}`);
          }
        });

        styleEl.textContent = `/* MyUIbrix Dynamic Styles - Auto-generated */\n${cssBlocks.join('\n\n')}`;
        
        // Force browser to recalculate styles
        if (typeof document !== 'undefined') {
          // Trigger a reflow to ensure styles are applied immediately
          document.body.offsetHeight; // Read operation forces reflow
        }
      } catch (e) {
        console.error('[MyUIbrix] Style injection failed:', e);
      }
    };

    const loadConfigs = async () => {
      try {
        const data = await getPageElementConfigs(pageType);
        if (active) {
          const configMap: Record<string, string> = {};
          const visMap: Record<string, boolean> = {};
          const stylesMap: Record<string, Record<string, any>> = {};
          
          // Sort by display_order to get correct element order
          const sorted = [...data].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          const order = sorted.map(config => config.element_name);
          
          sorted.forEach(config => {
            configMap[config.element_name] = config.variant;
            visMap[config.element_name] = config.visible !== false;
            // Load saved styles from settings.styles and apply to state
            try {
              const st = (config.settings && (config.settings as any).styles) || {};
              if (st && typeof st === 'object' && Object.keys(st).length > 0) {
                stylesMap[config.element_name] = st as Record<string, any>;
              }
            } catch {}
            // Inject saved custom CSS (from settings.customCSS or settings.styles.customCSS)
            try {
              const css = String(((config.settings as any)?.customCSS) || ((config.settings as any)?.styles?.customCSS) || '').trim();
              const styleId = `custom-css-${config.element_name}`;
              const existing = document.getElementById(styleId);
              if (existing) existing.remove();
              if (css) {
                const style = document.createElement('style');
                style.id = styleId;
                const hasBlocks = /\{[^}]*\}|@media|@keyframes/.test(css);
                if (hasBlocks) {
                  style.textContent = css;
                } else {
                  const importantDecls = css
                    .split(';')
                    .map(s => s.trim())
                    .filter(Boolean)
                    .map(s => (/!important\s*$/.test(s) ? s : `${s} !important`))
                    .join(';\n            ');
                  style.textContent = `\n          [data-element="${config.element_name}"] {\n            ${importantDecls};\n          }\n        `;
                }
                document.head.appendChild(style);
              }
            } catch {}
          });
          
          setConfigs(configMap);
          setVisibility(visMap);
          if (Object.keys(stylesMap).length > 0) {
            setStyles(stylesMap);
          } else {
            setStyles({});
          }
          setElementOrder(order);
          // Apply style-pack body class
          try {
            const sp = configMap['style-pack'] || 'default';
            const body = document.body;
            // Remove any previous style-pack-* classes
            Array.from(body.classList)
              .filter(cls => cls.startsWith('style-pack-'))
              .forEach(cls => body.classList.remove(cls));
            body.classList.add(`style-pack-${sp}`);
          } catch {}
          // Inject style properties so they apply even without inline spreading
          updateInjectedStyleProps(stylesMap);
          
          // Apply initial order to DOM only in editor/preview mode
          const isEditingMode = (() => {
            try {
              if (typeof document !== 'undefined' && (document.body?.classList?.contains('myuibrix-edit-mode'))) return true;
              const params = new URLSearchParams(window.location.search);
              return params.get('myuibrix') === 'edit';
            } catch {
              return false;
            }
          })();

          if (order.length > 0 && isEditingMode) {
            requestAnimationFrame(() => {
              applyDOMOrder(order);
            });
          }
        }
      } catch (error) {
        console.error('Failed to load page element configs:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadConfigs();

    // Listen for live updates from MyUIbrix editor (ONLY in preview mode)
    const handleMyUIbrixChange = ((event: CustomEvent) => {
      const { elementName, variant, visible, previewMode, timestamp } = event.detail;
      
      // Only apply changes if in preview mode (editing)
      // This prevents production users from seeing draft changes
      if (previewMode) {
        console.log(`[usePageElementConfig] Variant change: ${elementName} -> ${variant}`);
        
        setConfigs(prev => ({
          ...prev,
          [elementName]: variant
        }));
        
        setVisibility(prev => ({
          ...prev,
          [elementName]: visible
        }));
        // If style-pack changed, toggle body class for live preview
        if (elementName === 'style-pack') {
          try {
            const body = document.body;
            Array.from(body.classList)
              .filter(cls => cls.startsWith('style-pack-'))
              .forEach(cls => body.classList.remove(cls));
            body.classList.add(`style-pack-${variant || 'default'}`);
          } catch {}
        }
        
        // Force React to re-render by incrementing refresh key
        setRefreshKey(prev => prev + 1);
      }
    }) as EventListener;

    // Listen for reorder events
    const handleMyUIbrixReorder = ((event: CustomEvent) => {
      const { order } = event.detail;
      setElementOrder(order);
      try {
        const inEdit = document.body?.classList?.contains('myuibrix-edit-mode') || false;
        if (inEdit) {
          applyDOMOrder(order);
        }
      } catch {
        // no-op
      }
    }) as EventListener;

    // Listen for style changes from VisualStylePanel
    const handleMyUIbrixStyleChange = ((event: CustomEvent) => {
      const { elementName, styles: newStyles, previewMode } = event.detail;
      
      if (previewMode) {
        console.log(`[MyUIbrix] Style change received for: ${elementName}`, newStyles);
        
        // Only update state - let React apply the styles through component rendering
        // This prevents conflicts with React's virtual DOM
        setStyles(prev => {
          const next = {
            ...prev,
            [elementName]: newStyles
          };
          
          // Also update injected CSS for global preview application
          // This ensures immediate visual feedback even before React re-renders
          requestAnimationFrame(() => {
            updateInjectedStyleProps(next);
          });
          
          // Live-inject custom CSS if provided, with enhanced specificity
          try {
            const css = String((newStyles && (newStyles as any).customCSS) || '').trim();
            const styleId = `custom-css-${elementName}`;
            const existing = document.getElementById(styleId);
            if (existing) existing.remove();
            if (css) {
              const style = document.createElement('style');
              style.id = styleId;
              const hasBlocks = /\{[^}]*\}|@media|@keyframes/.test(css);
              if (hasBlocks) {
                // User wrote full CSS rules - use as-is
                style.textContent = `/* Custom CSS for ${elementName} */\n${css}`;
              } else {
                // Simple declarations - wrap with high-specificity selector
                const importantDecls = css
                  .split(';')
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(s => (/!important\s*$/.test(s) ? s : `${s} !important`))
                  .join(';\n  ');
                style.textContent = `/* Custom CSS for ${elementName} */\nbody [data-element="${elementName}"],\n.container [data-element="${elementName}"] {\n  ${importantDecls};\n}`;
              }
              document.head.appendChild(style);
            }
          } catch (e) {
            console.error(`[MyUIbrix] Custom CSS injection failed for ${elementName}:`, e);
          }
          
          return next;
        });
        
        // Force React re-render of affected component by incrementing refresh key
        setRefreshKey(prev => prev + 1);
      }
    }) as EventListener;

    window.addEventListener('myuibrix-change', handleMyUIbrixChange);
    window.addEventListener('myuibrix-reorder', handleMyUIbrixReorder);
    window.addEventListener('myuibrix-style-change', handleMyUIbrixStyleChange);
    // Support force refresh event from editorController
    const handleForceRefresh = ((event: CustomEvent) => {
      updateInjectedStyleProps(styles);
    }) as EventListener;
    window.addEventListener('myuibrix-force-refresh', handleForceRefresh);

    return () => {
      active = false;
      window.removeEventListener('myuibrix-change', handleMyUIbrixChange);
      window.removeEventListener('myuibrix-reorder', handleMyUIbrixReorder);
      window.removeEventListener('myuibrix-style-change', handleMyUIbrixStyleChange);
      window.removeEventListener('myuibrix-force-refresh', handleForceRefresh);
      try {
        const s = document.getElementById('myuibrix-style-props');
        if (s) s.remove();
      } catch {}
    };
  }, [pageType]);

  const getVariant = (elementName: string, defaultVariant: string = 'unified'): string => {
    return configs[elementName] || defaultVariant;
  };

  const isVisible = (elementName: string, defaultVisible: boolean = true): boolean => {
    return visibility[elementName] !== undefined ? visibility[elementName] : defaultVisible;
  };

  const getStyles = (elementName: string): Record<string, any> | undefined => {
    return styles[elementName];
  };

  return { configs, visibility, styles, elementOrder, getVariant, isVisible, getStyles, loading, refreshKey };
};
